import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import HeroSearch from "../components/HeroSearch";
import FeatureContainers from "../components/FeatureContainers";
import SyncBoardIntro from "../components/SyncBoardIntro";
import WhyAgencies from "../components/WhyAgencies";
import EnterpriseNews from "../components/EnterpriseNews";
import MasonryGrid from "../components/MasonryGrid";
import PhotoDrawer from "../components/PhotoDrawer";
import Footer from "../components/Footer";
import { curatedPhotos } from "../lib/api";

const PER_PAGE = 24;

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("photo");
  const nav = useNavigate();

  useEffect(() => {
    if (openId) nav(`/dashboard/photo/${openId}`, { replace: true });
  }, [openId, nav]);

  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialPhoto, setInitialPhoto] = useState(null);

  const load = useCallback(async (pg) => {
    setLoading(true);
    try {
      const data = await curatedPhotos({ page: pg, per_page: PER_PAGE });
      const arr = data.photos || [];
      setPhotos((prev) => {
        if (pg === 1) return arr;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...arr.filter((p) => !seen.has(p.id))];
      });
      setHasMore(Boolean(data.next_page) && arr.length > 0);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const loadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const openPhoto = (p) => nav(`/dashboard/photo/${p.id}`);
  const closeDrawer = () => {};

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <HeroSearch />

      <FeatureContainers />

      <SyncBoardIntro />

      <WhyAgencies />

      <EnterpriseNews />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 mt-16 md:mt-20 pb-24">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground">
              Editors&apos; picks
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-ink mt-2">
              Today&apos;s curated picks.
            </h2>
          </div>
          <div className="hidden md:block text-sm text-muted-foreground max-w-sm text-right">
            A rolling selection from our editorial team. Refreshed continuously.
          </div>
        </div>

        <MasonryGrid
          photos={photos}
          onOpen={openPhoto}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          testId="curated-masonry"
        />
      </section>

      <Footer />

      <PhotoDrawer
        open={Boolean(openId)}
        photoId={openId}
        initialPhoto={initialPhoto && String(initialPhoto.id) === String(openId) ? initialPhoto : null}
        onClose={closeDrawer}
        onOpenPhoto={openPhoto}
      />
    </div>
  );
}
